'use client';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { Badge } from '@/components/ui/badge';
import { Numeric } from '@/components/ui/numeric';
import { LocalDateTime } from '@/components/ui/LocalDateTime';
import { useHydratedTimeZone } from '@/hooks/useHydratedTimeZone';
import { useNowMs } from '@/hooks/useNowMs';
import { groupPlayerJourneyDays } from '../../libs/playerJourneyDays';
import { formatDayKey, todayKeyInZone } from '../../libs/matchesDay';
import { eventPlayerHref, type EventPlayer } from '../../libs/eventPlayers';
import type { PlayerJourney } from '../../libs/playerJourney';
import { NAME_WRAP } from '../../libs/nameBreaks';
import { displayMatchCode } from '../../libs/matchCode';
import type { JourneyContext } from './JourneyFixtureCard';
import { NameText } from './NameText';
const time = (iso: string, locale: string, timeZone?: string) =>
  new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone,
  }).format(new Date(iso));

export function PlayerJourneyRun({
  journey,
  opponents,
  division,
  ...context
}: JourneyContext & { journey: PlayerJourney; opponents: EventPlayer[]; division: string }) {
  const t = useTranslations('Event.playerJourney');
  const tMatches = useTranslations('Matches');
  const zone = useHydratedTimeZone() ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const now = useNowMs(30_000);
  const today = now === null ? null : todayKeyInZone(now, zone);
  const days = groupPlayerJourneyDays(journey, zone, today);
  return (
    <section
      data-player-run
      aria-label={t('run')}
      className="bg-card border-border min-w-0 rounded-xl border p-4 md:p-6"
    >
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-heading font-semibold">{t('run')}</h2>
        <span className="text-muted text-xs">
          {t('venueTime')} · {zone}
        </span>
      </div>
      {days.length ? (
        <ol className="divide-border divide-y">
          {days.map((day) => (
            <li
              key={day.day ?? day.kind}
              className="grid min-w-0 gap-2 py-3 first:pt-0 sm:grid-cols-[minmax(0,5rem)_minmax(0,1fr)]"
            >
              <h3
                className="text-muted flex flex-wrap gap-x-2 text-xs font-semibold sm:block"
                suppressHydrationWarning
              >
                {day.day === today && (
                  <span className="text-foreground sm:block">{t('today')}</span>
                )}
                {day.day
                  ? formatDayKey(day.day, context.locale)
                  : t(day.kind === 'played' ? 'playedMatches' : 'unscheduled')}
              </h3>
              {day.fixtures.length ? (
                <ol className="min-w-0 space-y-2">
                  {day.fixtures.map((fixture) => {
                    const opponent =
                      fixture.opponentIds.length === 1
                        ? opponents.find((p) => p.playerId === fixture.opponentIds[0])
                        : null;
                    const next = fixture.displayStatus === 'next',
                      live = fixture.displayStatus === 'live',
                      done = !!fixture.result;
                    const variant = live
                      ? 'warning'
                      : next
                        ? 'outline'
                        : fixture.result === 'lost' || fixture.result === 'walkoverLost'
                          ? 'destructive'
                          : fixture.result === 'won' || fixture.result === 'walkoverWon'
                            ? 'success'
                            : 'outline';
                    return (
                      <li
                        key={fixture.id}
                        data-fixture-id={fixture.id}
                        data-status={fixture.displayStatus}
                        className={`flex min-w-0 items-stretch overflow-hidden rounded-xl border ${next || live ? 'border-primary' : 'border-border'} ${done ? 'bg-surface' : 'bg-card'}`}
                      >
                        <div
                          className={`border-border flex shrink-0 basis-[clamp(3.5rem,20%,4.75rem)] flex-col items-center justify-center gap-1 border-r border-dashed px-1 py-3 text-center ${live ? 'bg-primary text-foreground' : ''}`}
                        >
                          <Numeric className="text-sm font-semibold">
                            {fixture.scheduledAt ? (
                              <LocalDateTime
                                iso={fixture.scheduledAt}
                                locale={context.locale}
                                format={time}
                              />
                            ) : (
                              '—'
                            )}
                          </Numeric>
                          <span className="text-muted max-w-full text-xs font-semibold break-words">
                            {fixture.courtName ?? t('courtPending')}
                          </span>
                        </div>
                        <div className="grid min-w-0 flex-1 gap-x-2 gap-y-1 px-3 py-2 sm:grid-cols-[minmax(0,1fr)_auto]">
                          <div
                            className={`min-w-0 text-sm font-semibold ${NAME_WRAP} ${done ? 'text-muted' : 'text-foreground'}`}
                            dir="auto"
                          >
                            {opponent ? (
                              <Link
                                className="inline-flex min-h-11 max-w-full items-center underline-offset-4 hover:underline"
                                href={`/${context.locale}${eventPlayerHref(context.eventId, context.slug, opponent.playerId, division)}`}
                              >
                                <span className="min-w-0">
                                  <NameText name={opponent.label} />
                                </span>
                              </Link>
                            ) : (
                              <span>{t('opponentPending')}</span>
                            )}
                          </div>
                          <div className="col-start-1 flex min-w-0 flex-wrap items-center justify-between gap-2 sm:contents">
                            <p className="text-muted min-w-0 text-xs break-words sm:col-start-1 sm:row-start-2">
                              {[
                                displayMatchCode(fixture.drawMatchId),
                                fixture.venueName,
                                fixture.stageName,
                                fixture.stageType === 'elimination' ? fixture.roundName : null,
                              ]
                                .filter(Boolean)
                                .join(' · ')}
                            </p>
                            <Badge
                              variant={variant}
                              className="max-w-full text-xs whitespace-normal sm:col-start-2 sm:row-span-2 sm:row-start-1 sm:self-center"
                            >
                              <span>
                                {t(
                                  fixture.displayStatus === 'next'
                                    ? 'nextShort'
                                    : fixture.displayStatus,
                                )}
                              </span>
                              {fixture.score && <Numeric className="ml-1">{fixture.score}</Numeric>}
                              {fixture.retired && (
                                <span className="ml-1" data-retired-marker>
                                  {tMatches('retiredShort')}
                                </span>
                              )}
                            </Badge>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              ) : (
                <p className="text-muted text-sm">{t('noMatchesToday')}</p>
              )}
            </li>
          ))}
        </ol>
      ) : (
        <p className="text-muted text-sm">{t('noResults')}</p>
      )}
      <div className="border-border text-muted mt-4 border-t pt-3 text-xs">
        <Numeric>
          {t('runSummary', {
            played: journey.run.filter((f) => ['won', 'lost', 'drawn'].includes(f.result)).length,
            won: journey.run.filter((f) => f.result === 'won').length,
            lost: journey.run.filter((f) => f.result === 'lost').length,
          })}
        </Numeric>
      </div>
    </section>
  );
}
